/**
 * SubmissionReconciliationService.js
 * Handles submission outcome reconciliation for lender add-project flows when
 * the initial submit returns 504 or any 5xx response.
 *
 * Feature: Submission Outcome Reconciliation (504 Timeout Recovery)
 * Endpoint: POST /lender/request/submission-outcome
 * Always-on – no feature flag required.
 */
import axios from 'axios';

/**
 * Polling back-off delays in milliseconds (4 bounded retries after the initial call).
 * R4: exactly [2000, 4000, 8000, 16000] ms.
 */
export const POLL_DELAYS = [2000, 4000, 8000, 16000];

/**
 * Builds the request payload for POST /lender/request/submission-outcome.
 *
 * @param {string} lenderId     - Required seller/servicer number.
 * @param {string|null} tempId  - Temporary correlation key; included only when truthy.
 * @param {Object} projectInfo  - Project/phase details from the form or Redux state.
 *   @param {string} [projectInfo.phaseName]      - Maps to projectName field.
 *   @param {string} [projectInfo.streetAddress]  - Maps to street field.
 *   @param {string} [projectInfo.city]
 *   @param {string} [projectInfo.state]
 *   @param {string} [projectInfo.zip5]
 * @returns {Object} Payload object ready to POST.
 */
export function buildReconciliationPayload(lenderId, tempId, projectInfo) {
  if (!lenderId) {
    throw new Error('buildReconciliationPayload: lenderId is required');
  }

  const info = projectInfo || {};

  const payload = {
    lenderId,
    projectName: info.phaseName || '',
    street: info.streetAddress || '',
    city: info.city || '',
    state: info.state || '',
    zip5: info.zip5 || '',
    timeWindowMinutes: 30,
  };

  if (tempId) {
    payload.correlationKey = tempId;
  }

  return payload;
}

/**
 * Maps a raw API outcome + HTTP status to a NormalizedSubmissionOutcome resolutionState string.
 *
 * Outcome values from API: FOUND | PROCESSING | NOT_FOUND | AMBIGUOUS
 * HTTP error codes handled: 400 -> reconciliationValidationError, 5xx -> reconciliationServerError
 *
 * @param {string|null} outcome    - The `outcome` field from the API response body (or null on error).
 * @param {number}      httpStatus - The HTTP response status code.
 * @param {string}      [requestStatus] - The `requestStatus` field (e.g. 'Submitted', 'Draft').
 * @returns {string} resolutionState
 */
export function normalizeOutcome(outcome, httpStatus, requestStatus) {
  if (httpStatus === 400) {
    return 'reconciliationValidationError';
  }
  if (httpStatus >= 500) {
    return 'reconciliationServerError';
  }

  switch (outcome) {
    case 'FOUND': {
      const status = (requestStatus || '').toLowerCase();
      if (status === 'draft') {
        return 'successDraft';
      }
      return 'successSubmitted';
    }
    case 'PROCESSING':
      return 'processingRetrying';
    case 'AMBIGUOUS':
      return 'ambiguousManualReview';
    case 'NOT_FOUND':
      return 'unresolvedNotFound';
    default:
      return 'reconciliationServerError';
  }
}

// ─── Private helpers ──────────────────────────────────────────────────────────

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function callReconciliationEndpoint(payload) {
  const response = await axios.post(
    global.REACT_APP_API_ENDPOINT + '/lender/request/submission-outcome',
    payload
  );
  return { ...response.data, httpStatus: response.status };
}

function getResolvedRequestId(apiResult) {
  return apiResult?.requestMasterTrackerId || apiResult?.masterTrackerId || null;
}

function getTerminalSuccess(apiResult) {
  return !!(apiResult?.isTerminalSuccess || apiResult?.terminalSuccess);
}

function hasResolvedIdentifiers(apiResult) {
  return !!(apiResult?.projectId || getResolvedRequestId(apiResult));
}

function isValidReconciliationPayload(apiResult) {
  if (!apiResult || typeof apiResult !== 'object') {
    return false;
  }

  if (!apiResult.outcome) {
    return false;
  }

  if (apiResult.outcome === 'FOUND' && !hasResolvedIdentifiers(apiResult)) {
    return false;
  }

  return true;
}

function buildNormalizedResult(apiResult, httpStatus, totalAttempts) {
  const resolutionState = normalizeOutcome(
    apiResult.outcome,
    httpStatus,
    apiResult.requestStatus
  );
  console.log('[submission_reconciliation_terminal]', {
    outcome: apiResult.outcome,
    requestStatus: apiResult.requestStatus || null,
    isTerminalSuccess: getTerminalSuccess(apiResult),
    resolvedProjectIdPresent: !!apiResult.projectId,
    resolvedRequestIdPresent: !!getResolvedRequestId(apiResult),
    totalAttempts,
  });
  return {
    resolutionState,
    resolvedProjectId: apiResult.projectId || null,
    resolvedRequestId: getResolvedRequestId(apiResult),
    userMessage: apiResult.message || null,
    requestStatus: apiResult.requestStatus || null,
    isTerminalSuccess: getTerminalSuccess(apiResult),
    httpStatus,
  };
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Reconcile submission outcome after a 504/5xx submit failure.
 * Performs one initial call plus up to POLL_DELAYS.length bounded retries for PROCESSING.
 * Returns a NormalizedSubmissionOutcome object.
 *
 * R1 gate: caller is responsible for invoking this only when submit status >= 500.
 * R4: uses POLL_DELAYS exactly.
 * R5: exhausted PROCESSING maps to unresolvedNotFound.
 * R8: 400 → reconciliationValidationError; 500 → reconciliationServerError.
 *
 * @param {object} payload - Result of buildReconciliationPayload()
 * @returns {Promise<{resolutionState, resolvedProjectId, resolvedRequestId, userMessage, requestStatus, isTerminalSuccess, httpStatus}>}
 */
export async function reconcileSubmissionOutcome(payload) {
  console.log('[submission_reconciliation_started]', {
    lenderId: payload.lenderId,
    hasCorrelationKey: !!payload.correlationKey,
    hasFallbackProjectName: !!payload.projectName,
    fallbackAddressFieldCount: [payload.street, payload.city, payload.state, payload.zip5].filter(Boolean).length,
  });

  for (let attempt = 0; attempt <= POLL_DELAYS.length; attempt++) {
    if (attempt > 0) {
      await sleep(POLL_DELAYS[attempt - 1]);
    }

    console.log('[submission_reconciliation_poll_attempt]', {
      attemptNumber: attempt + 1,
      delayMs: attempt > 0 ? POLL_DELAYS[attempt - 1] : 0,
      maxAttempts: POLL_DELAYS.length + 1,
      lenderId: payload.lenderId,
    });

    try {
      const result = await callReconciliationEndpoint(payload);
      const { httpStatus } = result;

      if (!isValidReconciliationPayload(result)) {
        console.warn('[submission_reconciliation_invalid_payload]', {
          attemptNumber: attempt + 1,
          httpStatus,
          hasOutcome: !!result?.outcome,
          hasProjectId: !!result?.projectId,
          hasRequestMasterTrackerId: !!getResolvedRequestId(result),
        });
        continue;
      }

      // Terminal outcomes — stop immediately
      if (
        result.outcome === 'FOUND' ||
        result.outcome === 'AMBIGUOUS' ||
        result.outcome === 'NOT_FOUND'
      ) {
        return buildNormalizedResult(result, httpStatus, attempt + 1);
      }

      // PROCESSING — continue polling if attempts remain
    } catch (err) {
      const errStatus = err?.response?.status || 500;
      const isRetryable = errStatus >= 500;

      console.error('[submission_reconciliation_api_error]', {
        httpStatus: errStatus,
        isRetryable,
        retryScheduled: isRetryable,
        attemptNumber: attempt + 1,
      });

      if (isRetryable) {
        // R8/AC10: one ~10 s delayed retry before surfacing reconciliationServerError
        await sleep(10000);
        console.log('[submission_reconciliation_poll_attempt]', {
          attemptNumber: 'api_error_retry',
          delayMs: 10000,
          maxAttempts: 1,
          lenderId: payload.lenderId,
        });
        try {
          const retryResult = await callReconciliationEndpoint(payload);
          const { httpStatus: retryStatus } = retryResult;

          if (!isValidReconciliationPayload(retryResult)) {
            console.warn('[submission_reconciliation_invalid_payload]', {
              attemptNumber: 'api_error_retry',
              httpStatus: retryStatus,
              hasOutcome: !!retryResult?.outcome,
              hasProjectId: !!retryResult?.projectId,
              hasRequestMasterTrackerId: !!getResolvedRequestId(retryResult),
            });
            continue;
          }

          if (
            retryResult.outcome === 'FOUND' ||
            retryResult.outcome === 'AMBIGUOUS' ||
            retryResult.outcome === 'NOT_FOUND'
          ) {
            return buildNormalizedResult(retryResult, retryStatus, attempt + 2);
          }

          continue;
        } catch (retryErr) {
          const retryStatus = retryErr?.response?.status || 500;
          console.error('[submission_reconciliation_api_error]', {
            httpStatus: retryStatus,
            isRetryable: false,
            retryScheduled: false,
            attemptNumber: 'api_error_retry',
          });
          return {
            resolutionState: normalizeOutcome(null, retryStatus, null),
            resolvedProjectId: null,
            resolvedRequestId: null,
            userMessage:
              retryErr?.response?.data?.error ||
              'An error occurred while checking your submission status.',
            requestStatus: null,
            isTerminalSuccess: false,
            httpStatus: retryStatus,
          };
        }
      }

      // 4xx from reconciliation endpoint — not retryable
      return {
        resolutionState: normalizeOutcome(null, errStatus, null),
        resolvedProjectId: null,
        resolvedRequestId: null,
        userMessage:
          err?.response?.data?.error ||
          'An error occurred while checking your submission status.',
        requestStatus: null,
        isTerminalSuccess: false,
        httpStatus: errStatus,
      };
    }
  }

  // All attempts exhausted — still PROCESSING — treat as NOT_FOUND (R5)
  console.log('[submission_reconciliation_terminal]', {
    outcome: 'PROCESSING_EXHAUSTED',
    requestStatus: null,
    isTerminalSuccess: false,
    resolvedProjectIdPresent: false,
    resolvedRequestIdPresent: false,
    totalAttempts: POLL_DELAYS.length + 1,
  });

  return {
    resolutionState: 'unresolvedNotFound',
    resolvedProjectId: null,
    resolvedRequestId: null,
    userMessage:
      'Could not confirm submission status after retries. Please check your Requests List to verify.',
    requestStatus: null,
    isTerminalSuccess: false,
    httpStatus: 200,
  };
}
